/**
 * DataCompletenessModal — Issue #648
 * -----------------------------------
 * Shown when a user activates the "Mietwagen in Anspruch genommen" checkbox but
 * one or more required fields are missing from the Gutachten-Akte.
 *
 * Only the missing fields (fehlend) are shown as inline inputs.
 * Three exit paths:
 *   - Speichern:          saves missing fields to DB, then calls onSaved
 *   - Abbrechen:          closes modal, checkbox stays unchecked
 *   - Trotzdem fortfahren: proceeds without saving, calls onProceedAnyway
 */

import { useState } from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import { FELD_LABELS } from '@/lib/mietwagen-crosslink';

export interface DataCompletenessModalProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  onProceedAnyway: () => void;
  akteId: string;
  fehlend: string[];
}

export default function DataCompletenessModal({
  open,
  onClose,
  onSaved,
  onProceedAnyway,
  akteId,
  fehlend,
}: DataCompletenessModalProps) {
  const [values, setValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(fehlend.map((f) => [f, ''])),
  );
  const [saving, setSaving] = useState(false);

  async function handleSave() {
    if (saving) return;
    const stillMissing = fehlend.filter((f) => !values[f]?.trim());
    if (stillMissing.length > 0) {
      toast.error('Bitte alle Pflichtfelder ausfüllen');
      return;
    }
    setSaving(true);
    try {
      const updates: Record<string, string> = {};
      for (const field of fehlend) {
        updates[field] = values[field].trim();
      }
      updates.updated_at = new Date().toISOString();

      const { error } = await supabase
        .from('akten')
        .update(updates)
        .eq('id', akteId);

      if (error) throw error;
      onSaved();
    } catch (err) {
      toast.error('Fehler beim Speichern', {
        description: err instanceof Error ? err.message : 'Unbekannter Fehler',
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent
        className="sm:max-w-md"
        aria-label="Pflichtdaten ergänzen"
        data-testid="data-completeness-modal"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-500" />
            Pflichtdaten unvollständig
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <p className="text-sm text-gray-600">
            Bitte ergänze die fehlenden Daten, damit die Mietwagen-Verknüpfung korrekt funktioniert.
          </p>

          {fehlend.map((field) => {
            const label = FELD_LABELS[field as keyof typeof FELD_LABELS] ?? field;
            const isDate = field === 'schadendatum';
            return (
              <div key={field} className="space-y-1">
                <label
                  htmlFor={`dcm-field-${field}`}
                  className="text-[11px] text-gray-500 uppercase tracking-wide"
                >
                  {label}
                </label>
                <input
                  id={`dcm-field-${field}`}
                  type={isDate ? 'date' : 'text'}
                  value={values[field] ?? ''}
                  onChange={(e) => setValues((prev) => ({ ...prev, [field]: e.target.value }))}
                  className="w-full border border-gray-200 rounded px-2 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-300"
                  placeholder={label}
                />
              </div>
            );
          })}

          <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-yellow-50 border border-yellow-200">
            <AlertTriangle className="w-3.5 h-3.5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <p className="text-[12px] text-yellow-800">
              Ohne vollständige Daten kann die Mietwagen-Akte nicht korrekt zugeordnet werden.
            </p>
          </div>
        </div>

        <DialogFooter className="flex-col gap-2 sm:flex-row">
          <button
            type="button"
            onClick={onProceedAnyway}
            disabled={saving}
            className="text-[12px] text-yellow-700 underline hover:text-yellow-900 self-center sm:mr-auto disabled:opacity-50"
          >
            Trotzdem fortfahren
          </button>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="px-3 py-1.5 text-sm text-gray-600 border border-gray-200 rounded hover:bg-gray-50"
          >
            Abbrechen
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="px-3 py-1.5 text-sm text-white bg-amber-500 rounded hover:bg-amber-600 disabled:opacity-50 flex items-center gap-1.5"
          >
            {saving && <Loader2 className="w-3 h-3 animate-spin" />}
            Speichern
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
